<template>
    <div class="MenuDropdown" v-click-outside="handleClickOutside">
        <div class="MenuDropdown-Trigger" ref="trigger" @click="toggle">
            <slot name="trigger" :isOpen="isOpen" :openUpward="openUpward">
                <button class="MenuDropdown-Button">
                    <slot name="button-content" :isOpen="isOpen" :openUpward="openUpward">
                        <span>{{ title }}</span>
                        <i
                            :class="[
                                openUpward ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line',
                                { 'is-open': isOpen },
                            ]"
                        ></i>
                    </slot>
                </button>
            </slot>
        </div>
        <div
            ref="content"
            class="MenuDropdown-Content"
            :class="{ 'is-open': isOpen, 'open-upward': openUpward, 'is-morphing': isMorphing }"
        >
            <div
                class="MenuDropdown-List"
                :class="{ 'items-at-top': itemsAtTop, 'open-upward': openUpward }"
            >
                <div
                    v-for="(item, index) in sortedItems"
                    :key="index"
                    class="MenuDropdown-Item"
                    :class="{
                        'is-clickable': item.to || item.href || item.action || item.click,
                        'is-active': item.to && $route.path === item.to,
                    }"
                    :style="{ '--item-index': index }"
                    @click="handleItemClick(item)"
                >
                    <i v-if="item.icon" :class="item.icon" class="MenuDropdown-ItemIcon"></i>
                    <span class="MenuDropdown-ItemLabel">{{ item.label }}</span>
                    <span
                        v-if="item.tooltip"
                        class="MenuDropdown-InfoIcon"
                        tabindex="0"
                        @click.stop
                    >
                        <i class="ri-information-line"></i>
                        <span class="MenuDropdown-Tooltip" :class="tooltipPosition">
                            <slot name="tooltip" :item="item" :index="index">
                                {{ item.tooltip }}
                            </slot>
                        </span>
                    </span>
                    <i
                        v-if="(item.to || item.href) && item.showArrow !== false"
                        class="ri-arrow-right-s-line MenuDropdown-ItemArrow"
                    ></i>
                    <span v-if="item.shortcut" class="MenuDropdown-ItemShortcut">{{
                        item.shortcut
                    }}</span>
                </div>
                <slot name="items"></slot>
            </div>
        </div>
        <MorphingGhost
            :fromElement="triggerElement"
            :toElement="contentElement"
            :isOpen="isOpen"
            :zIndex="8"
            :useRelative="useRelative"
            @animation-complete="onAnimationComplete"
        />
    </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';
import MorphingGhost from '@/components/animations/MorphingGhost.vue';

export default {
    name: 'MenuDropdown',
    components: {
        MorphingGhost,
    },
    directives: {
        ClickOutside,
    },
    props: {
        title: {
            type: String,
            default: '',
        },
        items: {
            type: Array,
            default: () => [],
        },
        defaultOpen: {
            type: Boolean,
            default: false,
        },
        tooltipPosition: {
            type: String,
            default: 'top',
            validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value),
        },
        itemsAtTop: {
            type: Boolean,
            default: false,
        },
        openUpward: {
            type: Boolean,
            default: false,
        },
        useRelative: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isOpen: this.defaultOpen,
            isMorphing: false,
            triggerElement: null,
            contentElement: null,
        };
    },
    mounted() {
        this.triggerElement = this.$refs.trigger;
        this.contentElement = this.$refs.content;
    },
    computed: {
        sortedItems() {
            return this.items;
        },
    },
    methods: {
        toggle() {
            this.isOpen = !this.isOpen;
            this.$emit('toggle', this.isOpen);
            if (this.isOpen) {
                this.$nextTick(() => {
                    this.isMorphing = true;
                });
            }
        },
        onAnimationComplete() {
            this.isMorphing = false;
        },
        handleItemClick(item) {
            if (item.action) {
                this.$emit('item-click', item);
                item.action();
                this.isOpen = false;
            } else if (item.click) {
                this.$emit('item-click', item);
                item.click();
                this.isOpen = false;
            } else if (item.to) {
                this.$emit('item-click', item);
                this.$router.push(item.to);
                this.isOpen = false;
            } else if (item.href) {
                this.$emit('item-click', item);
                window.location.href = item.href;
            }
        },
        handleClickOutside(event) {
            if (this.$el && this.$el.contains(event.target)) {
                return;
            }
            if (this.isOpen) {
                this.isOpen = false;
                this.$emit('toggle', false);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.MenuDropdown {
    position: relative;
}

.MenuDropdown-Trigger {
    cursor: pointer;
}

.MenuDropdown-Button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: linear-gradient(135deg, #f0f4ff 0%, #e6eeff 100%);
    border: 1px solid rgba(59, 130, 246, 0.15);
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--color-blue-dark);
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    gap: 8px;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.08);

    i {
        font-size: 1rem;
        transition: transform 0.2s ease;
        flex-shrink: 0;
        color: var(--color-blue-dark);
        transform: rotate(180deg);

        &.is-open {
            transform: rotate(0deg);
        }
    }

    &:hover {
        background: linear-gradient(135deg, #e6eeff 0%, #dee8ff 100%);
        border-color: rgba(59, 130, 246, 0.25);
        box-shadow: 0 6px 16px rgba(59, 130, 246, 0.12);
    }
}

.MenuDropdown-Content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: absolute;
    left: 0;
    right: 0;
    z-index: 10;
    background: var(--color-white);
    border-radius: 6px;
    padding: 0;
    max-height: 0;
    overflow: hidden;
    overflow: hidden;
    transition: opacity 0.4s ease 0.1s;
    opacity: 0;
    pointer-events: none;
    box-sizing: border-box;
    transform-origin: top center;

    top: calc(100% + 4px);
    bottom: auto;
    align-items: stretch;
    justify-content: flex-start;

    &.is-open {
        max-height: 500px;
        opacity: 1;
        padding: 8px;
        pointer-events: auto;
        overflow: visible;
        transform: scaleY(1);
    }

    &.is-morphing {
        background: transparent !important;
        border-color: transparent !important;
        box-shadow: none !important;
    }

    &.open-upward {
        top: unset;
        bottom: calc(100% + 4px);
        justify-content: flex-start;
        align-items: stretch;
        transform-origin: bottom center;
        transform: scaleY(0);
    }

    &.open-upward.is-open {
        transform: scaleY(1);
    }
}

.MenuDropdown-List {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &.items-at-top {
        flex-direction: column-reverse;
    }

    &.open-upward {
        justify-content: flex-start;
        align-items: stretch;
    }
}

.MenuDropdown-Item {
    font-size: 0.875rem;
    color: var(--color-text-gray-dark);
    background: transparent;
    border-radius: 6px;
    padding: 7px 12px;
    font-weight: 400;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: space-between;
    flex-shrink: 0;
    box-sizing: border-box;
    opacity: 0;
    transform: translateY(-8px) scale(0.96);
    transition:
        opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1),
        transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    transition-delay: calc(var(--item-index) * 0.03s);

    .MenuDropdown-Content.is-open & {
        opacity: 1;
        transform: translateY(0) scale(1);
    }

    .MenuDropdown-Content.open-upward & {
        transform: translateY(8px) scale(0.96);
    }

    .MenuDropdown-Content.open-upward.is-open & {
        opacity: 1;
        transform: translateY(0) scale(1);
    }

    &.is-clickable {
        cursor: pointer;

        &:hover {
            background: var(--color-hover);
            color: var(--color-blue-dark);
            transition:
                background-color 0.15s ease,
                color 0.15s ease,
                transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .MenuDropdown-Content.is-open & {
            &:hover {
                transform: translateY(0) scale(1) translateX(2px);
            }
        }

        .MenuDropdown-Content.open-upward.is-open & {
            &:hover {
                transform: translateY(0) scale(1) translateX(2px);
            }
        }
    }

    &.is-active {
        background: var(--color-blue-light);
        color: var(--color-blue-dark);
        font-weight: 600;
    }
}

.MenuDropdown-ItemIcon {
    font-size: 1rem;
    flex-shrink: 0;
    color: var(--color-text-gray-dark);
    width: 20px;
    text-align: center;
}

.MenuDropdown-ItemArrow {
    font-size: 0.875rem;
    flex-shrink: 0;
    color: var(--color-text-gray-dark);
    opacity: 0.6;
}

.MenuDropdown-ItemShortcut {
    font-size: 0.75rem;
    color: var(--color-text-gray-dark);
    opacity: 0.6;
    flex-shrink: 0;
    margin-left: auto;
}

.MenuDropdown.theme-dark {
    .MenuDropdown-Button {
        background: linear-gradient(
            135deg,
            rgba(235, 241, 255, 0.12) 0%,
            rgba(245, 248, 255, 0.05) 100%
        );
        border: 1px solid rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(8px);
        color: rgba(255, 255, 255, 0.95);

        i {
            color: rgba(255, 255, 255, 0.95);
        }

        &:hover {
            background: linear-gradient(
                135deg,
                rgba(235, 241, 255, 0.18) 0%,
                rgba(245, 248, 255, 0.08) 100%
            );
            border-color: rgba(255, 255, 255, 0.2);
        }
    }

    .MenuDropdown-Content {
        background: var(--color-blue-darker);
        border: 0.5px solid rgba(120, 120, 120, 0.5);
    }

    .MenuDropdown-Item {
        color: rgba(255, 255, 255, 0.8);
        background: transparent;

        &.is-clickable {
            &:hover {
                background: rgba(255, 255, 255, 0.1);
                color: rgba(255, 255, 255, 1);
            }
        }

        &.is-active {
            background: rgba(255, 255, 255, 0.15);
            color: rgba(255, 255, 255, 1);
            font-weight: 600;
        }
    }

    .MenuDropdown-ItemIcon {
        color: rgba(255, 255, 255, 0.8);
    }

    .MenuDropdown-ItemArrow {
        color: rgba(255, 255, 255, 0.6);
    }

    .MenuDropdown-ItemShortcut {
        color: rgba(255, 255, 255, 0.6);
    }

    .MenuDropdown-ItemLabel {
        color: rgba(255, 255, 255, 0.8);
    }

    .MenuDropdown-InfoIcon {
        color: rgba(255, 255, 255, 0.8);

        i {
            color: rgba(255, 255, 255, 0.8);
        }

        &:hover i,
        &:focus i {
            color: rgba(255, 255, 255, 1);
        }
    }

    .MenuDropdown-Tooltip {
        background: var(--color-blue-darker);
        color: rgba(255, 255, 255, 0.9);
        border: 0.5px solid rgba(255, 255, 255, 0.1);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
    }
}

.MenuDropdown-ItemLabel {
    flex: 1;
}

.MenuDropdown-InfoIcon {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    position: relative;
    color: var(--color-text-gray-dark);
    font-size: 0.875rem;
    outline: none;
    flex-shrink: 0;

    i {
        font-size: 0.875rem;
    }

    &:hover i,
    &:focus i {
        color: var(--color-blue-dark);
    }

    &:hover .MenuDropdown-Tooltip,
    &:focus .MenuDropdown-Tooltip {
        display: block;
        pointer-events: auto;
    }
}

.MenuDropdown-Tooltip {
    display: none;
    position: absolute;
    min-width: 220px;
    max-width: 280px;
    background: #fff;
    color: #2a3457;
    border: 0.5px solid #e3e4ea;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(30, 34, 90, 0.1);
    padding: 10px 14px;
    font-size: 0.97rem;
    z-index: 20;
    white-space: normal;
    pointer-events: none;
    line-height: 1.4;

    &.top {
        left: 50%;
        bottom: 120%;
        transform: translateX(-50%);
    }

    &.bottom {
        left: 50%;
        top: 120%;
        transform: translateX(-50%);
    }

    &.left {
        right: 120%;
        top: 50%;
        transform: translateY(-50%);
    }

    &.right {
        left: 120%;
        top: 50%;
        transform: translateY(-50%);
    }

    @media (max-width: 600px) {
        &.top,
        &.bottom {
            left: 0;
            right: 0;
            transform: none;
            min-width: 160px;
            font-size: 0.93rem;
        }
    }
}
</style>
